import React from 'react';
import { X, CheckCircle, XCircle, Clock } from 'lucide-react';

const HospitalDetails = ({ hospital, onClose, onStatusChange }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-auto">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800">Hospital Details</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6">
          {/* Status badge and actions */}
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-500">Status:</span>
              {hospital.status === 'approved' ? (
                <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 flex items-center">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Approved
                </span>
              ) : hospital.status === 'rejected' ? (
                <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 flex items-center">
                  <XCircle className="w-3 h-3 mr-1" />
                  Rejected
                </span>
              ) : (
                <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800 flex items-center">
                  <Clock className="w-3 h-3 mr-1" />
                  Pending
                </span>
              )}
            </div>
            
            <div className="flex space-x-2">
              {hospital.status !== 'approved' && (
                <button
                  onClick={() => onStatusChange(hospital._id, 'approved')}
                  className="px-3 py-1 bg-green-50 text-green-700 rounded-md text-sm hover:bg-green-100 flex items-center"
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Approve
                </button>
              )}
              {hospital.status !== 'rejected' && (
                <button
                  onClick={() => onStatusChange(hospital._id, 'rejected')}
                  className="px-3 py-1 bg-red-50 text-red-700 rounded-md text-sm hover:bg-red-100 flex items-center"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Reject
                </button>
              )}
              {hospital.status !== 'pending' && (
                <button
                  onClick={() => onStatusChange(hospital._id, 'pending')}
                  className="px-3 py-1 bg-yellow-50 text-yellow-700 rounded-md text-sm hover:bg-yellow-100 flex items-center"
                >
                  <Clock className="w-4 h-4 mr-1" />
                  Reset to Pending
                </button>
              )}
            </div>
          </div>
          
          {/* Hospital details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-500">Hospital Name</p>
                  <p className="mt-1">{hospital.name}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-500">Registration Number</p>
                  <p className="mt-1">{hospital.registrationNumber || 'Not provided'}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-500">Email</p>
                  <p className="mt-1">{hospital.email}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-500">Phone</p>
                  <p className="mt-1">{hospital.phone}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-500">Website</p>
                  <p className="mt-1">
                    {hospital.website ? (
                      <a 
                        href={hospital.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {hospital.website}
                      </a>
                    ) : (
                      'Not provided'
                    )}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-500">Blood Donation Center</p>
                  <p className="mt-1">
                    {hospital.hasBloodDonationCenter ? 'Yes' : 'No'}
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Address & Location</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-500">Street</p>
                  <p className="mt-1">{hospital.address?.street || 'Not provided'}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-500">City</p>
                  <p className="mt-1">{hospital.address?.city}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-500">State</p>
                  <p className="mt-1">{hospital.address?.state}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-500">Postal Code</p>
                  <p className="mt-1">{hospital.address?.postalCode || 'Not provided'}</p>
                </div>
              </div>
              
              <h3 className="text-lg font-medium text-gray-900 mt-6 mb-4">Additional Information</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-500">License Number</p>
                  <p className="mt-1">{hospital.licenseNumber || 'Not provided'}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-500">Registration Date</p>
                  <p className="mt-1">
                    {hospital.registrationDate 
                      ? new Date(hospital.registrationDate).toLocaleDateString() 
                      : 'Not available'}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Special instructions or notes */}
          {hospital.specialInstructions && (
            <div className="mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Special Instructions</h3>
              <div className="p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-700">{hospital.specialInstructions}</p>
              </div>
            </div>
          )}
        </div>
        
        <div className="border-t px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default HospitalDetails;
